import React from "react";

/* ================================================================
   Flat 2D Characters — 8 animated character action templates
   Walking, running, jumping, waving, sitting, pointing, dancing, swimming
   Simple flat cartoon style with smooth motion
   ================================================================ */

// ── 1. Walking Character ─────────────────────────────────────────────
export const WalkingCharacter: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const legSwing = Math.sin(phase * 2) * 18;
  const armSwing = Math.sin(phase * 2) * 15;
  const bounce = Math.abs(Math.sin(phase * 2)) * 4;
  return (
    <svg viewBox="0 0 400 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Ground */}
      <line x1="0" y1="350" x2="400" y2="350" stroke="#475569" strokeWidth="2" />
      {/* Footprints */}
      {[80, 140, 200, 260].map((fx, i) => (
        <ellipse key={i} cx={fx} cy="348" rx="8" ry="3" fill="#334155" opacity="0.3" />
      ))}
      <g transform={`translate(200,${300 - bounce})`}>
        {/* Body */}
        <rect x="-12" y="-60" width="24" height="35" rx="8" fill="#3b82f6" />
        {/* Head */}
        <circle cx="0" cy="-75" r="16" fill="#fbbf24" />
        {/* Eyes */}
        <circle cx="-5" cy="-78" r="2" fill="#1e293b" />
        <circle cx="5" cy="-78" r="2" fill="#1e293b" />
        {/* Smile */}
        <path d="M-5,-70 Q0,-66 5,-70" fill="none" stroke="#1e293b" strokeWidth="1.5" />
        {/* Left arm */}
        <line x1="-12" y1="-50" x2={-20 + armSwing} y2={-25 + Math.abs(armSwing) * 0.3} stroke="#fbbf24" strokeWidth="5" strokeLinecap="round" />
        {/* Right arm */}
        <line x1="12" y1="-50" x2={20 - armSwing} y2={-25 + Math.abs(armSwing) * 0.3} stroke="#fbbf24" strokeWidth="5" strokeLinecap="round" />
        {/* Left leg */}
        <line x1="-6" y1="-25" x2={-8 + legSwing} y2="0" stroke="#1e293b" strokeWidth="5" strokeLinecap="round" />
        {/* Right leg */}
        <line x1="6" y1="-25" x2={8 - legSwing} y2="0" stroke="#1e293b" strokeWidth="5" strokeLinecap="round" />
      </g>
      <text x="200" y="385" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Walking</text>
    </svg>
  );
};

// ── 2. Running Character ─────────────────────────────────────────────
export const RunningCharacter: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const legSwing = Math.sin(phase * 3) * 25;
  const armSwing = Math.sin(phase * 3) * 22;
  const bounce = Math.abs(Math.sin(phase * 3)) * 8;
  const lean = 8;
  return (
    <svg viewBox="0 0 400 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <line x1="0" y1="350" x2="400" y2="350" stroke="#475569" strokeWidth="2" />
      {/* Speed lines */}
      {[0, 1, 2].map((i) => (
        <line key={i} x1={110 - i * 20} y1={260 + i * 20} x2={140 - i * 20} y2={260 + i * 20} stroke="#94a3b8" strokeWidth="2" opacity="0.3">
          <animate attributeName="x1" values={`${110 - i * 20};${80 - i * 20};${110 - i * 20}`} dur="0.5s" repeatCount="indefinite" />
        </line>
      ))}
      <g transform={`translate(200,${300 - bounce}) rotate(${lean})`}>
        {/* Body */}
        <rect x="-12" y="-60" width="24" height="35" rx="8" fill="#ef4444" />
        {/* Head */}
        <circle cx="0" cy="-75" r="16" fill="#fbbf24" />
        <circle cx="-4" cy="-78" r="2" fill="#1e293b" />
        <circle cx="6" cy="-78" r="2" fill="#1e293b" />
        <path d="M-3,-70 Q2,-65 7,-70" fill="none" stroke="#1e293b" strokeWidth="1.5" />
        {/* Arms - pumping */}
        <line x1="-12" y1="-50" x2={-25 + armSwing} y2={-35} stroke="#fbbf24" strokeWidth="5" strokeLinecap="round" />
        <line x1="12" y1="-50" x2={25 - armSwing} y2={-35} stroke="#fbbf24" strokeWidth="5" strokeLinecap="round" />
        {/* Legs - wide stride */}
        <line x1="-6" y1="-25" x2={-12 + legSwing} y2="5" stroke="#1e293b" strokeWidth="5" strokeLinecap="round" />
        <line x1="6" y1="-25" x2={12 - legSwing} y2="5" stroke="#1e293b" strokeWidth="5" strokeLinecap="round" />
      </g>
      <text x="200" y="385" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Running</text>
    </svg>
  );
};

// ── 3. Jumping Character ─────────────────────────────────────────────
export const JumpingCharacter: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const jumpCycle = Math.sin(phase * 1.2);
  const jumpH = Math.max(0, jumpCycle) * 60;
  const armUp = Math.max(0, jumpCycle) * 30;
  const legSpread = Math.max(0, jumpCycle) * 12;
  return (
    <svg viewBox="0 0 400 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <line x1="0" y1="350" x2="400" y2="350" stroke="#475569" strokeWidth="2" />
      {/* Shadow */}
      <ellipse cx="200" cy="347" rx={20 + jumpH * 0.2} ry={4} fill="#1e293b" opacity={0.3 - jumpH * 0.003} />
      <g transform={`translate(200,${300 - jumpH})`}>
        {/* Body */}
        <rect x="-12" y="-60" width="24" height="35" rx="8" fill="#22c55e" />
        {/* Head */}
        <circle cx="0" cy="-75" r="16" fill="#fbbf24" />
        <circle cx="-5" cy="-78" r="2" fill="#1e293b" />
        <circle cx="5" cy="-78" r="2" fill="#1e293b" />
        {/* Happy mouth when jumping */}
        <path d={jumpH > 10 ? "M-6,-70 Q0,-63 6,-70" : "M-4,-70 Q0,-67 4,-70"} fill="none" stroke="#1e293b" strokeWidth="1.5" />
        {/* Arms up */}
        <line x1="-12" y1="-50" x2={-22} y2={-55 - armUp} stroke="#fbbf24" strokeWidth="5" strokeLinecap="round" />
        <line x1="12" y1="-50" x2={22} y2={-55 - armUp} stroke="#fbbf24" strokeWidth="5" strokeLinecap="round" />
        {/* Legs */}
        <line x1="-6" y1="-25" x2={-10 - legSpread} y2="0" stroke="#1e293b" strokeWidth="5" strokeLinecap="round" />
        <line x1="6" y1="-25" x2={10 + legSpread} y2="0" stroke="#1e293b" strokeWidth="5" strokeLinecap="round" />
      </g>
      <text x="200" y="385" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Jumping</text>
    </svg>
  );
};

// ── 4. Waving Character ──────────────────────────────────────────────
export const WavingCharacter: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const waveAngle = Math.sin(phase * 2.5) * 25;
  return (
    <svg viewBox="0 0 400 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <line x1="0" y1="350" x2="400" y2="350" stroke="#475569" strokeWidth="2" />
      <g transform="translate(200,300)">
        {/* Body */}
        <rect x="-12" y="-60" width="24" height="35" rx="8" fill="#f59e0b" />
        {/* Head */}
        <circle cx="0" cy="-75" r="16" fill="#fbbf24" />
        <circle cx="-5" cy="-78" r="2" fill="#1e293b" />
        <circle cx="5" cy="-78" r="2" fill="#1e293b" />
        <path d="M-5,-70 Q0,-65 5,-70" fill="none" stroke="#1e293b" strokeWidth="1.5" />
        {/* Left arm - relaxed */}
        <line x1="-12" y1="-50" x2="-20" y2="-25" stroke="#fbbf24" strokeWidth="5" strokeLinecap="round" />
        {/* Right arm - waving */}
        <g transform={`rotate(${-40 + waveAngle},12,-50)`}>
          <line x1="12" y1="-50" x2="12" y2="-85" stroke="#fbbf24" strokeWidth="5" strokeLinecap="round" />
          {/* Hand */}
          <circle cx="12" cy="-90" r="5" fill="#fbbf24" />
        </g>
        {/* Legs */}
        <line x1="-6" y1="-25" x2="-8" y2="0" stroke="#1e293b" strokeWidth="5" strokeLinecap="round" />
        <line x1="6" y1="-25" x2="8" y2="0" stroke="#1e293b" strokeWidth="5" strokeLinecap="round" />
      </g>
      {/* "Hi!" text */}
      <text x="260" y="200" fill="#f59e0b" fontSize="18" fontWeight="bold" opacity={0.5 + Math.sin(phase * 2) * 0.3}>
        Hi!
      </text>
      <text x="200" y="385" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Waving Hello</text>
    </svg>
  );
};

// ── 5. Sitting Reading ───────────────────────────────────────────────
export const SittingReading: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  return (
    <svg viewBox="0 0 400 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <line x1="0" y1="350" x2="400" y2="350" stroke="#475569" strokeWidth="2" />
      {/* Chair */}
      <rect x="150" y="260" width="80" height="10" rx="3" fill="#78350f" />
      <rect x="220" y="210" width="10" height="90" rx="3" fill="#78350f" />
      <rect x="155" y="270" width="8" height="80" rx="2" fill="#78350f" />
      <rect x="217" y="270" width="8" height="80" rx="2" fill="#78350f" />
      {/* Character sitting */}
      <g>
        {/* Torso */}
        <rect x="168" y="215" width="24" height="35" rx="8" fill="#8b5cf6" />
        {/* Head */}
        <circle cx="180" cy="200" r="16" fill="#fbbf24" />
        <circle cx="175" cy="197" r="2" fill="#1e293b" />
        <circle cx="185" cy="197" r="2" fill="#1e293b" />
        {/* Looking down at book */}
        <path d="M175,204 Q180,207 185,204" fill="none" stroke="#1e293b" strokeWidth="1" />
        {/* Arms holding book */}
        <line x1="168" y1="225" x2="155" y2="240" stroke="#fbbf24" strokeWidth="5" strokeLinecap="round" />
        <line x1="192" y1="225" x2="205" y2="240" stroke="#fbbf24" strokeWidth="5" strokeLinecap="round" />
        {/* Legs (sitting, bent) */}
        <line x1="174" y1="250" x2="170" y2="265" stroke="#1e293b" strokeWidth="5" strokeLinecap="round" />
        <line x1="170" y1="265" x2="165" y2="285" stroke="#1e293b" strokeWidth="4" strokeLinecap="round" />
        <line x1="186" y1="250" x2="190" y2="265" stroke="#1e293b" strokeWidth="5" strokeLinecap="round" />
        <line x1="190" y1="265" x2="195" y2="285" stroke="#1e293b" strokeWidth="4" strokeLinecap="round" />
      </g>
      {/* Book */}
      <rect x="148" y="235" width="30" height="22" rx="2" fill="#e0e7ff" stroke="#94a3b8" strokeWidth="1" transform="rotate(-10,163,246)" />
      <rect x="178" y="235" width="30" height="22" rx="2" fill="#f1f5f9" stroke="#94a3b8" strokeWidth="1" transform="rotate(10,193,246)" />
      {/* Page turning occasionally */}
      <line x1="178" y1="235" x2="178" y2="257" stroke="#94a3b8" strokeWidth="1" />
      {/* Thought bubble */}
      <circle cx="230" cy="175" r="4" fill="#e0e7ff" opacity="0.4" />
      <circle cx="240" cy="160" r="6" fill="#e0e7ff" opacity="0.3" />
      <ellipse cx="260" cy="140" rx="25" ry="15" fill="#e0e7ff" opacity="0.2" />
      <text x="260" y="144" textAnchor="middle" fill="#6366f1" fontSize="8" opacity={0.3 + Math.sin(phase * 0.5) * 0.2}>💡</text>
      <text x="200" y="385" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Reading</text>
    </svg>
  );
};

// ── 6. Pointing Teacher ──────────────────────────────────────────────
export const PointingTeacher: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const pointBob = Math.sin(phase * 1.5) * 3;
  return (
    <svg viewBox="0 0 500 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Board */}
      <rect x="250" y="80" width="200" height="150" rx="4" fill="#065f46" stroke="#92400e" strokeWidth="4" />
      <text x="350" y="140" textAnchor="middle" fill="#d1fae5" fontSize="14">Key Concept</text>
      <text x="350" y="170" textAnchor="middle" fill="#a7f3d0" fontSize="10">A + B = C</text>
      {/* Teacher */}
      <g transform={`translate(180,${300 + pointBob})`}>
        {/* Body */}
        <rect x="-14" y="-65" width="28" height="40" rx="8" fill="#1e293b" />
        {/* Tie */}
        <polygon points="-2,-65 2,-65 0,-50" fill="#ef4444" />
        {/* Head */}
        <circle cx="0" cy="-82" r="18" fill="#fbbf24" />
        {/* Glasses */}
        <circle cx="-7" cy="-84" r="5" fill="none" stroke="#1e293b" strokeWidth="1.5" />
        <circle cx="7" cy="-84" r="5" fill="none" stroke="#1e293b" strokeWidth="1.5" />
        <line x1="-2" y1="-84" x2="2" y2="-84" stroke="#1e293b" strokeWidth="1" />
        <circle cx="-7" cy="-84" r="1.5" fill="#1e293b" />
        <circle cx="7" cy="-84" r="1.5" fill="#1e293b" />
        <path d="M-4,-75 Q0,-72 4,-75" fill="none" stroke="#1e293b" strokeWidth="1" />
        {/* Left arm - down */}
        <line x1="-14" y1="-55" x2="-22" y2="-30" stroke="#fbbf24" strokeWidth="5" strokeLinecap="round" />
        {/* Right arm - pointing at board */}
        <line x1="14" y1="-55" x2="60" y2={-80 + pointBob * 2} stroke="#fbbf24" strokeWidth="5" strokeLinecap="round" />
        {/* Pointer stick */}
        <line x1="55" y1={-78 + pointBob * 2} x2="110" y2={-100 + pointBob} stroke="#78350f" strokeWidth="2" />
        {/* Legs */}
        <line x1="-6" y1="-25" x2="-8" y2="0" stroke="#1e293b" strokeWidth="5" strokeLinecap="round" />
        <line x1="6" y1="-25" x2="8" y2="0" stroke="#1e293b" strokeWidth="5" strokeLinecap="round" />
      </g>
      <text x="250" y="380" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Teaching</text>
    </svg>
  );
};

// ── 7. Dancing Character ─────────────────────────────────────────────
export const DancingCharacter: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const sway = Math.sin(phase * 2) * 12;
  const armAngle = Math.sin(phase * 2.5) * 30;
  const legBend = Math.sin(phase * 2) * 10;
  return (
    <svg viewBox="0 0 400 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <line x1="0" y1="350" x2="400" y2="350" stroke="#475569" strokeWidth="2" />
      {/* Music notes */}
      {["♪", "♫", "♪", "♫"].map((n, i) => (
        <text key={i} x={100 + i * 70} y={80 + Math.sin(phase * 1.5 + i) * 15} fill="#ec4899" fontSize="16" opacity="0.4">
          {n}
        </text>
      ))}
      {/* Disco floor effect */}
      {[0, 1, 2, 3, 4].map((i) => (
        <rect key={i} x={120 + i * 35} y="340" width="30" height="10" rx="2" fill={["#ef4444", "#3b82f6", "#22c55e", "#f59e0b", "#8b5cf6"][i]} opacity={0.1 + Math.sin(phase * 2 + i) * 0.1} />
      ))}
      <g transform={`translate(${200 + sway},300)`}>
        {/* Body */}
        <rect x="-12" y="-60" width="24" height="35" rx="8" fill="#ec4899" />
        {/* Head */}
        <circle cx="0" cy="-75" r="16" fill="#fbbf24" />
        <circle cx="-5" cy="-78" r="2" fill="#1e293b" />
        <circle cx="5" cy="-78" r="2" fill="#1e293b" />
        <path d="M-6,-70 Q0,-63 6,-70" fill="none" stroke="#1e293b" strokeWidth="1.5" />
        {/* Arms - disco style */}
        <line x1="-12" y1="-50" x2={-25 - armAngle * 0.5} y2={-70 - Math.abs(armAngle)} stroke="#fbbf24" strokeWidth="5" strokeLinecap="round" />
        <line x1="12" y1="-50" x2={25 + armAngle * 0.5} y2={-40 + armAngle} stroke="#fbbf24" strokeWidth="5" strokeLinecap="round" />
        {/* Legs */}
        <line x1="-6" y1="-25" x2={-12 + legBend} y2="0" stroke="#1e293b" strokeWidth="5" strokeLinecap="round" />
        <line x1="6" y1="-25" x2={12 - legBend} y2="0" stroke="#1e293b" strokeWidth="5" strokeLinecap="round" />
      </g>
      <text x="200" y="385" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Dancing</text>
    </svg>
  );
};

// ── 8. Swimming Character ────────────────────────────────────────────
export const SwimmingCharacter: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const strokeAngle = phase * 2;
  const leftArm = Math.sin(strokeAngle) * 40;
  const rightArm = Math.sin(strokeAngle + Math.PI) * 40;
  const kick = Math.sin(strokeAngle * 2) * 12;
  return (
    <svg viewBox="0 0 600 300" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Water */}
      <rect x="0" y="80" width="600" height="220" fill="#1e3a8a" rx="12" />
      {/* Waves on surface */}
      <path d="M0,80 Q50,70 100,80 Q150,90 200,80 Q250,70 300,80 Q350,90 400,80 Q450,70 500,80 Q550,90 600,80" fill="none" stroke="#60a5fa" strokeWidth="2">
        <animate attributeName="d" values="M0,80 Q50,70 100,80 Q150,90 200,80 Q250,70 300,80 Q350,90 400,80 Q450,70 500,80 Q550,90 600,80;M0,80 Q50,90 100,80 Q150,70 200,80 Q250,90 300,80 Q350,70 400,80 Q450,90 500,80 Q550,70 600,80;M0,80 Q50,70 100,80 Q150,90 200,80 Q250,70 300,80 Q350,90 400,80 Q450,70 500,80 Q550,90 600,80" dur="3s" repeatCount="indefinite" />
      </path>
      {/* Swimmer horizontal */}
      <g transform="translate(300,140)">
        {/* Body */}
        <ellipse cx="0" cy="0" rx="30" ry="10" fill="#3b82f6" />
        {/* Head */}
        <circle cx="35" cy="-5" r="12" fill="#fbbf24" />
        <circle cx="40" cy="-7" r="1.5" fill="#1e293b" />
        {/* Left arm */}
        <line x1="10" y1="-5" x2={10 + leftArm * 0.7} y2={-15 - Math.abs(leftArm) * 0.5} stroke="#fbbf24" strokeWidth="4" strokeLinecap="round" />
        {/* Right arm */}
        <line x1="-5" y1="-5" x2={-5 + rightArm * 0.7} y2={-15 - Math.abs(rightArm) * 0.5} stroke="#fbbf24" strokeWidth="4" strokeLinecap="round" />
        {/* Legs / kick */}
        <line x1="-25" y1="3" x2="-45" y2={8 + kick} stroke="#fbbf24" strokeWidth="4" strokeLinecap="round" />
        <line x1="-25" y1="3" x2="-45" y2={8 - kick} stroke="#fbbf24" strokeWidth="4" strokeLinecap="round" />
      </g>
      {/* Bubbles */}
      {[0, 1, 2].map((i) => (
        <circle key={i} cx={260 - i * 15} cy={155 + i * 10} r={2 + i} fill="#60a5fa" opacity="0.4">
          <animate attributeName="cy" values={`${155 + i * 10};${130 + i * 10};${155 + i * 10}`} dur={`${1.5 + i * 0.3}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.4;0.1;0.4" dur={`${1.5 + i * 0.3}s`} repeatCount="indefinite" />
        </circle>
      ))}
      {/* Splash lines */}
      <path d="M330,72 Q335,65 340,72" fill="none" stroke="#60a5fa" strokeWidth="1.5" opacity="0.4">
        <animate attributeName="opacity" values="0;0.4;0" dur="1s" repeatCount="indefinite" />
      </path>
      <text x="300" y="280" textAnchor="middle" fill="#93c5fd" fontSize="14" fontWeight="bold">Swimming</text>
    </svg>
  );
};
